/**
 * 戦闘力サマリーブロック (メンバー詳細ページのカード)。
 *
 * 期待値はシート (月度目標戦闘力 / pt plan / 【PDG】pt plan) を SSOT とする読み取り専用表示で、
 * Web からの編集・保存は廃止された。`canEdit` は「+ 新Q追加」操作の可否判定にのみ使用する。
 *
 * 実装は以下に分割:
 * - `TeamTable.tsx`: PDG / ACE のチーム別テーブル (読み取り専用)
 * - `RequirementDefinitionTable.tsx`: 要件定義ptを持つメンバーの内訳テーブル (全体ビューの PDG 列下)
 * - `combat-power-utils.ts`: Q パース / 整形 / 定数
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力サマリー (Q + 月選択 + チーム別) のオーケストレーション
 * @graph-connects RequirementDefinitionTable [calls] 全体ビューの PDG 列下に要件定義テーブルを出す
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { useAuth } from '../../../hooks/useAuth.js';
import {
  getCombatScope,
  canEditCombatPowerExpected,
  TET_TEAM_MEMBER_NICKNAMES,
  type TetSubTeam,
} from '@cortex/domain-product-innovation-lab-combat-power-scope';
import { getTeamOptionLabel } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import type { MonthlyCombatPower, CombatPowerResponse } from '../../../types/combat-power.js';
import { TeamTable } from './TeamTable.js';
import { QuarterTeamTable } from './QuarterTeamTable.js';
import { AceTeamTables } from './AceTeamTables.js';
import { RequirementDefinitionTable } from './RequirementDefinitionTable.js';
import { ADD_NEW_QUARTER_VALUE, nextQuarterTitle } from './combat-power-utils.js';

interface CombatPowerBlockProps {
  data: CombatPowerResponse;
  selectedMonthIdx: number;
  onMonthChange: (idx: number) => void;
  onSheetChange: (sheet: string) => void;
  /**
   * 担当者名 (小文字 nickName) → メールアドレスの対応表。
   * TeamTable に渡して担当者名をメンバー詳細ページへのリンクにするために使う。
   */
  nameToEmail?: Map<string, string>;
  /**
   * 表示スコープ。
   * - 未指定 (既定): PDG / ACE を左右に並べる (全体ビュー)。
   * - `'pdg'`: PDG テーブルのみ (PDG TE ビューへコピー)。
   * - `'ace'`: ACE をサブチーム別 (代表色見出し) に分割表示 (ACE ビューへコピー)。
   * - TE サブチーム (`'growth'` / `'experience'`): PDG の母集団を nick_name 名簿で
   *   絞った 1 テーブル。`'pdg'` と同じ見せ方で、見出しだけサブチーム名になる。
   */
  scope?: 'pdg' | 'ace' | TetSubTeam;
}

/** @graph-connects none */
export function CombatPowerBlock({
  data,
  selectedMonthIdx,
  onMonthChange,
  onSheetChange,
  nameToEmail,
  scope,
}: CombatPowerBlockProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // 「+ 新Q追加」操作の可否判定にのみ使用する (期待値の編集機能は廃止済み)。
  const canEdit = useMemo(
    () =>
      canEditCombatPowerExpected({
        portalRoles: user?.portalRoles,
        division: user?.division,
        teamName: user?.teamName,
      }),
    [user?.portalRoles, user?.division, user?.teamName],
  );

  /**
   * フロントだけで「+ 新Q追加」されたQタイトル群。BQ/シートには書かれていないが、
   * バックエンドの getCombatPowerData は parseQuarterTitle で受け付けてデフォルト値で
   * ビューを返してくれる (リロードで消える)。
   */
  const [clientAddedQuarters, setClientAddedQuarters] = useState<string[]>([]);

  /**
   * 四半期ビュー (メンバー × 月別の期待値/実績 横並び) の表示中フラグ。
   * 月タブをクリックすると月別ビューへ戻る。
   */
  const [isQuarterView, setIsQuarterView] = useState(false);

  const teamMap = useMemo(() => {
    const map = new Map<string, 'pdg' | 'ace'>();
    for (const m of data.memberTeamMap) {
      const scope = getCombatScope(m);
      if (scope) map.set(m.name, scope);
    }
    return map;
  }, [data.memberTeamMap]);

  /**
   * name(lower) → joinedAt の Map。null は英字フォールバック用に末尾扱い。
   * 入社順 (古いほど上、新しいほど下) で表示するためのキー。
   */
  const joinedAtMap = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const x of data.memberTeamMap) m.set(x.name, x.joinedAt);
    return m;
  }, [data.memberTeamMap]);

  // ACE サブチーム別表示 (scope='ace') 用の name(lower) → group/team マップ。
  const memberGroupTeamMap = useMemo(() => {
    const m = new Map<string, { group: string; team: string }>();
    for (const x of data.memberTeamMap) m.set(x.name, { group: x.group, team: x.team });
    return m;
  }, [data.memberTeamMap]);

  const sortByJoinedThenName = useCallback(
    (a: MonthlyCombatPower, b: MonthlyCombatPower): number => {
      const ja = joinedAtMap.get(a.name.toLowerCase()) ?? null;
      const jb = joinedAtMap.get(b.name.toLowerCase()) ?? null;
      if (ja !== null && jb !== null) {
        if (ja !== jb) return ja < jb ? -1 : 1;
      } else if (ja !== null) return -1;
      else if (jb !== null) return 1;
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    },
    [joinedAtMap],
  );

  const { pdgMembers, aceMembers } = useMemo(() => {
    const pdg: MonthlyCombatPower[] = [];
    const ace: MonthlyCombatPower[] = [];
    for (const m of data.members) {
      const team = teamMap.get(m.name.toLowerCase());
      if (team === 'pdg') pdg.push(m);
      else if (team === 'ace') ace.push(m);
    }
    pdg.sort(sortByJoinedThenName);
    ace.sort(sortByJoinedThenName);
    return { pdgMembers: pdg, aceMembers: ace };
  }, [data.members, teamMap, sortByJoinedThenName]);

  /**
   * 1 テーブルだけを出すスコープ（PDG 本体 / TE サブチーム）の表示内容。
   * ACE だけはサブチーム別に複数テーブルを出すため `AceTeamTables` 側で扱う。
   *
   * TE サブチームは team_name を持たず nick_name 名簿で割る括りなので、
   * PDG の母集団を名簿で絞り込む（名簿の正本は combat-power-scope の SSOT）。
   */
  const singleTeam = useMemo((): { label: string; members: MonthlyCombatPower[] } | null => {
    if (scope === 'pdg') return { label: 'PDG', members: pdgMembers };
    if (scope === undefined || scope === 'ace') return null;
    const roster = TET_TEAM_MEMBER_NICKNAMES[scope];
    return {
      label: getTeamOptionLabel(scope),
      members: pdgMembers.filter((m) => roster.has(m.name.toLowerCase())),
    };
  }, [scope, pdgMembers]);

  const sheetOptions = useMemo(() => {
    // クライアント側で追加した未来 Q は最新順で先頭側に並べ、その後に既存 Q (sheet/snapshot)
    const clientOptions = clientAddedQuarters
      .slice()
      .sort((a, b) => b.localeCompare(a))
      .map((title) => ({ value: title, label: title }));
    return [
      ...(canEdit
        ? [{ value: ADD_NEW_QUARTER_VALUE, label: t('members.combatPower.addNewQuarter') }]
        : []),
      ...clientOptions,
      ...data.quarterSheets.map((s) => ({
        value: s.title,
        // 下拉メニューの表示名はそのまま Q 名 (snapshot / sheet 由来の区別はラベルに出さない)
        label: s.title,
      })),
    ];
  }, [canEdit, clientAddedQuarters, data.quarterSheets, t]);

  const handleSelectChange = useCallback(
    (value: string | null) => {
      if (value === null) return;
      if (value === ADD_NEW_QUARTER_VALUE) {
        // 既存 Q + すでにクライアント追加済みの Q から次Q を算出 (重複追加防止)
        const knownTitles = [...data.quarterSheets.map((q) => q.title), ...clientAddedQuarters];
        const nextTitle = nextQuarterTitle(knownTitles);
        if (!nextTitle) return;
        if (!clientAddedQuarters.includes(nextTitle)) {
          setClientAddedQuarters((prev) => [...prev, nextTitle]);
        }
        onSheetChange(nextTitle);
        return;
      }
      onSheetChange(value);
    },
    [clientAddedQuarters, data.quarterSheets, onSheetChange],
  );

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('members.combatPower.title')}
        action={
          // Q ドロップダウンはヘッダ右上に配置 (最大 8 件 ≒ 16rem を表示しその先はスクロール)
          <div
            className="min-w-[180px]"
            style={{ '--cortex-mp-list-max-height': '16rem' } as React.CSSProperties}
          >
            <Picker
              options={sheetOptions}
              value={data.selectedSheet}
              onChange={handleSelectChange}
              aria-label={t('members.combatPower.title')}
            />
          </div>
        }
      />
      <CardContent>
        {data.localDryRunMode && (
          <div
            className="mb-3 px-3 py-2 rounded text-xs font-medium border"
            style={{
              borderColor: 'var(--color-warning)',
              backgroundColor: 'rgba(234, 179, 8, 0.08)',
              color: 'var(--color-warning)',
            }}
          >
            🛡 {t('members.combatPower.dryRunBanner')}
          </div>
        )}
        {/* Month selector tabs + 四半期ビュー切替 */}
        <div className="flex gap-2 mb-4">
          {data.monthHeaders.map((mh: string, i: number) => (
            <button
              key={mh}
              type="button"
              onClick={() => {
                setIsQuarterView(false);
                onMonthChange(i);
              }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                !isQuarterView && i === selectedMonthIdx
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              {mh}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setIsQuarterView(true);
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
              isQuarterView
                ? 'bg-[var(--color-accent-primary)] text-white'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            {t('members.combatPower.quarterView')}
          </button>
          {!isQuarterView && (
            <span className="text-xs text-[var(--color-text-muted)] self-center ml-2">
              {t('members.combatPower.baseDays')}: {data.baseBusinessDays[selectedMonthIdx] ?? '-'}
              {t('members.combatPower.days')}
            </span>
          )}
        </div>

        {/* スコープ別表示: ACE=サブチーム別 / 単一チーム(PDG・TEサブチーム)=1 テーブル /
            既定=PDG+ACE 左右並び。
            四半期ビューでは各テーブルを月別の期待値/実績 横並び表 (QuarterTeamTable) に切り替える */}
        {scope === 'ace' ? (
          <AceTeamTables
            data={data}
            memberTeamMap={memberGroupTeamMap}
            currentMonthIdx={selectedMonthIdx}
            nameToEmail={nameToEmail}
            quarterView={isQuarterView}
          />
        ) : singleTeam ? (
          isQuarterView ? (
            <QuarterTeamTable
              teamLabel={singleTeam.label}
              members={singleTeam.members}
              monthHeaders={data.monthHeaders}
              nameToEmail={nameToEmail}
            />
          ) : (
            <TeamTable
              teamLabel={singleTeam.label}
              members={singleTeam.members}
              currentMonthIdx={selectedMonthIdx}
              nameToEmail={nameToEmail}
            />
          )
        ) : isQuarterView ? (
          <div className="flex gap-6 flex-col lg:flex-row">
            <QuarterTeamTable
              teamLabel="PDG"
              members={pdgMembers}
              monthHeaders={data.monthHeaders}
              nameToEmail={nameToEmail}
            />
            <QuarterTeamTable
              teamLabel="ACE"
              members={aceMembers}
              monthHeaders={data.monthHeaders}
              nameToEmail={nameToEmail}
            />
          </div>
        ) : (
          <div className="flex gap-6 flex-col lg:flex-row">
            {/* PDG は ACE より人数が少なく下に空きが出るので、その空きに要件定義テーブルを置く。
                ラッパーは block（flex にしない）: flex にすると TeamTable の flex-1 が縦方向に
                効いて PDG テーブルが伸び、要件定義テーブルが列の最下部へ離れてしまう */}
            <div className="flex-1 min-w-0 space-y-6">
              <TeamTable
                teamLabel="PDG"
                members={pdgMembers}
                currentMonthIdx={selectedMonthIdx}
                nameToEmail={nameToEmail}
              />
              <RequirementDefinitionTable
                members={pdgMembers}
                currentMonthIdx={selectedMonthIdx}
                nameToEmail={nameToEmail}
                monthHeader={data.monthHeaders[selectedMonthIdx] ?? ''}
              />
            </div>
            <TeamTable
              teamLabel="ACE"
              members={aceMembers}
              currentMonthIdx={selectedMonthIdx}
              nameToEmail={nameToEmail}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
